import { TechNode } from '../../types';

/**
 * Energy domain nodes
 * The path to abundant, clean energy
 */
export const ENERGY_NODES: TechNode[] = [
  // === FOUNDATIONAL (Tier 0) ===
  {
    id: 'energy-solar-pv',
    name: 'Solar Photovoltaics',
    description: 'Direct conversion of sunlight to electricity',
    domain: 'energy',
    status: 'achieved',
    yearAchieved: 1954,
    importance: 7,
    tier: 0,
    trl: 9,
    progress: 100,
    milestones: [
      {
        date: '1954-04-25',
        title: 'Bell Labs Silicon Cell',
        description: 'First practical silicon PV cell (~6% efficiency) demonstrated',
        significance: 'breakthrough',
        source: 'https://www.aps.org/publications/apsnews/200904/physicshistory.cfm',
      },
      {
        date: '1958-03-17',
        title: 'Vanguard 1',
        description: 'First satellite powered by solar cells',
        significance: 'major',
        source: 'https://nssdc.gsfc.nasa.gov/nmc/spacecraft/display.action?id=1958-002B',
      },
    ],
  },
  {
    id: 'energy-wind',
    name: 'Wind Power',
    description: 'Grid-scale wind energy generation',
    domain: 'energy',
    status: 'achieved',
    yearAchieved: 1980,
    importance: 7,
    tier: 0,
    trl: 9,
    progress: 100,
    milestones: [
      {
        date: '1980-07-01',
        title: 'TV 3 Wind Turbine',
        description: 'First multi-megawatt grid-connected wind turbine (Denmark)',
        significance: 'breakthrough',
        source: 'https://en.wikipedia.org/wiki/TV_3_(wind_turbine)',
      },
      {
        date: '2023-01-01',
        title: 'Wind Passes 1 TW',
        description: 'Global installed wind capacity exceeds 1 terawatt',
        significance: 'major',
        source: 'https://www.gwec.net/global-wind-report-2023/',
      },
    ],
  },
  {
    id: 'energy-lithium-ion',
    name: 'Lithium-Ion Batteries',
    description: 'High-density rechargeable energy storage',
    domain: 'energy',
    status: 'achieved',
    yearAchieved: 1991,
    importance: 8,
    tier: 0,
    trl: 9,
    keyPlayers: ['Sony', 'Panasonic', 'CATL', 'Tesla'],
    milestones: [
      {
        date: '1991-01-01',
        title: 'Sony Commercial Li-ion',
        description: 'Sony ships first commercial lithium-ion battery (Goodenough/Yoshino)',
        significance: 'breakthrough',
        source: 'https://www.nobelprize.org/prizes/chemistry/2019/advanced-information/',
      },
      {
        date: '2019-10-09',
        title: 'Nobel Prize',
        description: 'Goodenough, Whittingham, Yoshino awarded for Li-ion development',
        significance: 'major',
        source: 'https://www.nobelprize.org/prizes/chemistry/2019/press-release/',
      },
    ],
  },

  // === CURRENT FRONTIER (Tier 1) ===
  {
    id: 'energy-grid-storage',
    name: 'Grid-Scale Storage',
    description: 'Utility-scale battery systems for grid stability',
    domain: 'energy',
    status: 'in_progress',
    importance: 8,
    tier: 1,
    trl: 6,
    dependencies: ['energy-lithium-ion'],
    keyPlayers: ['Tesla', 'Fluence', 'BYD'],
    progress: 60,
    progressMetric: 'Global Capacity',
    progressValue: '100+ GWh',
    milestones: [
      {
        date: '2017-12-01',
        title: 'Hornsdale Power Reserve',
        description: 'Tesla 100 MW / 129 MWh grid battery in South Australia',
        significance: 'breakthrough',
        source: 'https://hornsdalepowerreserve.com.au/',
      },
      {
        date: '2024-01-01',
        title: 'Global Grid Storage',
        description: 'Deployed utility-scale storage exceeds 100 GWh worldwide',
        significance: 'major',
        source: 'https://www.iea.org/reports/grid-scale-storage',
      },
    ],
  },
  {
    id: 'energy-perovskite',
    name: 'Perovskite Solar Cells',
    description: 'Next-gen solar with higher efficiency potential',
    domain: 'energy',
    status: 'in_progress',
    importance: 7,
    tier: 1,
    trl: 4,
    dependencies: ['energy-solar-pv'],
    progress: 50,
    progressMetric: 'Lab Efficiency',
    progressValue: '33.7%',
    milestones: [
      {
        date: '2023-01-01',
        title: '33.7% Lab Cell',
        description: 'Record perovskite single-junction efficiency (certified)',
        significance: 'major',
        source: 'https://www.nrel.gov/pv/cell-efficiency.html',
      },
      {
        date: '2024-06-01',
        title: 'Perovskite Tandem Progress',
        description: 'Silicon-perovskite tandems approach commercial pilot lines',
        significance: 'major',
        source: 'https://www.nrel.gov/pv/perovskite-solar-cells.html',
      },
    ],
  },
  {
    id: 'energy-solid-state',
    name: 'Solid-State Batteries',
    description: 'Higher density, safer battery technology',
    domain: 'energy',
    status: 'in_progress',
    importance: 8,
    tier: 1,
    trl: 4,
    dependencies: ['energy-lithium-ion'],
    keyPlayers: ['QuantumScape', 'Toyota', 'Samsung SDI'],
    progress: 40,
    yearEstimated: 2027,
    milestones: [
      {
        date: '2020-12-08',
        title: 'QuantumScape Solid-State',
        description: 'QuantumScape demonstrates anode-free solid-state cell in 1-layer form',
        significance: 'major',
        source: 'https://www.quantumscape.com/2020/12/quantumscape-announces-breakthrough-in-solid-state-battery-development/',
      },
      {
        date: '2024-01-01',
        title: 'Toyota Production Roadmap',
        description: 'Toyota announces solid-state EV production by 2027–2028',
        significance: 'major',
        source: 'https://global.toyota/en/newsroom/corporate/',
      },
    ],
  },

  // === FUSION (Tier 2-3) ===
  {
    id: 'energy-fusion-tokamak',
    name: 'Tokamak Fusion',
    description: 'Magnetic confinement fusion reactors',
    domain: 'energy',
    status: 'in_progress',
    importance: 9,
    tier: 2,
    trl: 5,
    keyPlayers: ['ITER', 'Commonwealth Fusion', 'TAE Technologies'],
    progress: 45,
    milestones: [
      {
        date: '2022-12-05',
        title: 'NIF Ignition',
        description: 'LLNL achieved first controlled fusion net energy gain (2.05 MJ in, 3.15 MJ out)',
        significance: 'breakthrough',
        source: 'https://www.llnl.gov/article/49306/lawrence-livermore-national-laboratory-achieves-fusion-ignition',
      },
      {
        date: '2025-01-01',
        title: 'ITER First Plasma',
        description: 'ITER tokamak targets first plasma (construction milestone)',
        significance: 'major',
        source: 'https://www.iter.org/proj/iterandbeyond',
      },
      {
        date: '2021-09-05',
        title: 'SPARC Magnet',
        description: 'Commonwealth Fusion demonstrates record high-field HTS magnet',
        significance: 'major',
        source: 'https://cfs.energy/news/cfs-achieves-major-fusion-milestone',
      },
    ],
  },
  {
    id: 'energy-fusion-net-gain',
    name: 'Net Energy Gain Fusion',
    description: 'Sustained fusion producing more energy than consumed',
    domain: 'energy',
    status: 'in_progress',
    importance: 10,
    tier: 3,
    trl: 5,
    dependencies: ['energy-fusion-tokamak'],
    keyPlayers: ['Commonwealth Fusion', 'Helion', 'TAE'],
    progress: 20,
    yearEstimated: 2030,
    milestones: [
      {
        date: '2024-06-01',
        title: 'NIF Reproducibility',
        description: 'LLNL reports repeated net gain shots with improved yield',
        significance: 'major',
        source: 'https://www.llnl.gov/article/49306/lawrence-livermore-national-laboratory-achieves-fusion-ignition',
      },
      {
        date: '2023-05-01',
        title: 'Helion Polaris',
        description: 'Helion targets net electricity from pulsed fusion by mid-2020s',
        significance: 'major',
        source: 'https://www.helionenergy.com/articles/helion-announces-purchase-agreement-with-microsoft/',
      },
    ],
  },
  {
    id: 'energy-fusion-commercial',
    name: 'Commercial Fusion Power',
    description: 'Economically viable fusion power plants',
    domain: 'energy',
    status: 'theoretical',
    importance: 10,
    tier: 4,
    trl: 3,
    dependencies: ['energy-fusion-net-gain'],
    yearEstimated: 2040,
  },

  // === ADVANCED (Tier 3+) ===
  {
    id: 'energy-smr',
    name: 'Small Modular Reactors',
    description: 'Factory-built, deployable nuclear reactors',
    domain: 'energy',
    status: 'in_progress',
    importance: 8,
    tier: 2,
    trl: 6,
    keyPlayers: ['NuScale', 'TerraPower', 'Rolls-Royce'],
    progress: 55,
    yearEstimated: 2029,
    milestones: [
      {
        date: '2023-12-31',
        title: 'NuScale NRC Approval',
        description: 'NuScale SMR design certified by US NRC',
        significance: 'breakthrough',
        source: 'https://www.nuscale.com/news/nrc-approves-nuscale-power-module-design',
      },
      {
        date: '2024-06-10',
        title: 'TerraPower Natrium',
        description: 'TerraPower breaks ground on Natrium SMR demonstration (Wyoming)',
        significance: 'major',
        source: 'https://www.terrapower.com/terrapower-breaks-ground-on-natrium-demo-plant/',
      },
    ],
  },
  {
    id: 'energy-space-solar',
    name: 'Space-Based Solar',
    description: 'Solar collection in orbit, beamed to Earth',
    domain: 'energy',
    status: 'theoretical',
    importance: 7,
    tier: 4,
    trl: 2,
    dependencies: ['energy-perovskite'],
  },
  {
    id: 'energy-wireless-power',
    name: 'Long-Range Wireless Power',
    description: 'Efficient wireless energy transmission over kilometers',
    domain: 'energy',
    status: 'theoretical',
    importance: 6,
    tier: 3,
    trl: 2,
  },
];

export default ENERGY_NODES;
